// Only declare the class if it hasn't been declared yet
if (typeof PCConfigurator === 'undefined') {
  window.PCConfigurator = class PCConfigurator {
    constructor(options = {}) {
      this.enablePriceBreakdown = options.enablePriceBreakdown !== undefined ? options.enablePriceBreakdown : true;
      this.collapseSectionsOnLoad = options.collapseSectionsOnLoad !== undefined ? options.collapseSectionsOnLoad : false;
      console.log('PC Configurator initialized with options:', options);
      this.shippingProductId = options.shippingProductId || null;
      this.backendUrl = options.backendUrl || 'https://pc-configurator-backend-production.up.railway.app';

      // Parse compatibility rules
      try {
        this.compatibilityRules = typeof options.compatibilityRules === 'string'
          ? JSON.parse(options.compatibilityRules)
          : (options.compatibilityRules || []);
      } catch (e) {
        this.compatibilityRules = [];
      }

      this.selectedComponents = {};
      this.totalPrice = 0;
      this.isDeselecting = false; // Flag to prevent change event during deselection

      this.init();
    }

    init() {
      // Initialize collapsible sections (all expanded by default)
      this.initializeCollapsibles();

      // Use event delegation for dynamically added radio buttons
      this.bindEvents();
      this.updateTotalPrice();
      this.updateSelectedComponentsList();
      this.updateAddToCartButton();

      // Re-bind and update when options are loaded
      this.setupOptionsLoadedListener();
    }

    initializeCollapsibles() {
      // Set sections to collapsed or expanded based on setting
      const isCollapsed = this.collapseSectionsOnLoad;
      document.querySelectorAll('.pc-configurator__component').forEach(component => {
        component.dataset.collapsed = isCollapsed ? 'true' : 'false';
        const button = component.querySelector('.pc-configurator__collapse-toggle');
        if (button) {
          button.setAttribute('aria-expanded', isCollapsed ? 'false' : 'true');
        }
      });
    }

    setupOptionsLoadedListener() {
      // Wait for all options to be rendered by JavaScript
      const checkAndInitialize = () => {
        const allComponents = document.querySelectorAll('.pc-configurator__component');
        const allOptions = document.querySelectorAll('.pc-configurator__option');

        // Wait until we have at least one option per component
        if (allComponents.length > 0 && allOptions.length >= allComponents.length) {
          this.initializeCollapsibles();
          this.bindEvents();
          this.initializeDefaultSelections();
          this.applyCompatibilityRules();
          this.updateTotalPrice();
          this.updateSelectedComponentsList();
          this.updateAddToCartButton();
        } else {
          // Retry after a short delay
          setTimeout(checkAndInitialize, 200);
        }
      };

      setTimeout(checkAndInitialize, 300);
    }

    initializeDefaultSelections() {
      // Clear existing selections first
      this.selectedComponents = {};

      // Get all checked radio buttons (first option in each category)
      const checkedRadios = document.querySelectorAll('.pc-configurator__option input[type="radio"]:checked');

      // Also get all default options (marked with is-default class) as fallback
      const defaultOptions = document.querySelectorAll('.pc-configurator__option.is-default');

      // Process checked radios first
      checkedRadios.forEach(radio => {
        const option = radio.closest('.pc-configurator__option');
        if (option) {
          const componentType = option.dataset.componentType;
          const optionName = option.dataset.optionName;
          const optionPrice = parseInt(option.dataset.optionPrice) || 0;
          const optionDescription = option.dataset.optionDescription || '';

          if (componentType) {
            this.selectedComponents[componentType.toLowerCase()] = {
              name: optionName,
              price: optionPrice,
              description: optionDescription
            };

            option.classList.add('selected');

            // Initialize data-was-checked for deselection functionality
            radio.dataset.wasChecked = 'true';

            // Update selected component name in header
            this.updateSelectedComponentName(componentType.toLowerCase(), optionName);

            // Initialize build preview image if this is the case component
            if (componentType.toLowerCase() === 'case') {
              this.updateBuildPreviewImage(option);
            }
          }
        }
      });

      // Process default options that weren't already processed
      defaultOptions.forEach(option => {
        const componentType = option.dataset.componentType;
        if (componentType && !this.selectedComponents[componentType.toLowerCase()]) {
          const optionName = option.dataset.optionName;
          const optionPrice = parseInt(option.dataset.optionPrice) || 0;
          const optionDescription = option.dataset.optionDescription || '';

          this.selectedComponents[componentType.toLowerCase()] = {
            name: optionName,
            price: optionPrice,
            description: optionDescription
          };

          option.classList.add('selected');

          // Ensure the radio is checked
          const radio = option.querySelector('input[type="radio"]');
          if (radio) {
            radio.checked = true;
            // Initialize data-was-checked for deselection functionality
            radio.dataset.wasChecked = 'true';
          }

          // Update selected component name in header
          this.updateSelectedComponentName(componentType.toLowerCase(), optionName);

          // Initialize build preview image if this is the case component
          if (componentType.toLowerCase() === 'case') {
            this.updateBuildPreviewImage(option);
          }
        }
      });
    }

    bindEvents() {
      // Use event delegation for dynamically added elements
      if (!this.changeHandlerBound) {
        // Handle click on option box to allow deselection
        document.addEventListener('click', (e) => {
          const optionBox = e.target.closest('.pc-configurator__option');
          if (optionBox) {
            // Prevent default radio button behavior
            e.preventDefault();

            const radio = optionBox.querySelector('input[type="radio"]');
            if (!radio) return;

            // Check if this option is disabled (incompatible)
            if (radio.disabled || optionBox.classList.contains('pc-configurator__option--disabled')) {
              return; // Don't allow selection of disabled options
            }

            const componentType = radio.name;

            // Check if this radio was already selected
            if (radio.checked && radio.dataset.wasChecked === 'true') {
              // Deselect it
              radio.checked = false;
              radio.dataset.wasChecked = 'false';
              this.handleComponentDeselection(componentType);
            } else {
              // Select it (whether it's unchecked or checked but not marked as wasChecked)
              // Mark all radios in this group as not checked
              document.querySelectorAll(`input[name="${componentType}"]`).forEach(r => {
                r.dataset.wasChecked = 'false';
              });
              // Mark this one as checked and handle selection
              radio.checked = true;
              radio.dataset.wasChecked = 'true';

              // Create a mock event for handleComponentSelection
              const mockEvent = { target: radio };
              this.handleComponentSelection(mockEvent);
            }
          }
        });

        this.changeHandlerBound = true;
      }

      // Use event delegation for collapsible toggles (header or button)
      if (!this.collapsibleHandlerBound) {
        document.addEventListener('click', (e) => {
          // Check if clicking the toggle button
          const button = e.target.closest('.pc-configurator__collapse-toggle');
          if (button) {
            e.stopPropagation();
            const toggle = button.closest('[data-collapsible-toggle]');
            if (toggle) {
              this.toggleCollapsible(toggle);
            }
            return;
          }

          // Check if clicking the header (but not the button)
          const toggle = e.target.closest('[data-collapsible-toggle]');
          if (toggle && !e.target.closest('.pc-configurator__collapse-toggle')) {
            this.toggleCollapsible(toggle);
          }
        });
        this.collapsibleHandlerBound = true;
      }

      // Bind add to cart button
      const addToCartBtn = document.querySelector('.pc-configurator__add-to-cart');
      if (addToCartBtn) {
        addToCartBtn.removeEventListener('click', this.addToCartBound);
        this.addToCartBound = () => this.addToCart();
        addToCartBtn.addEventListener('click', this.addToCartBound);
      }

      // Note: Event listeners removed to avoid conflicts during form submission
      // The updateCartItemImages() will be called manually when needed
    }

    toggleCollapsible(toggleElement) {
      const component = toggleElement.closest('.pc-configurator__component');
      if (!component) return;

      const isCollapsed = component.dataset.collapsed === 'true';
      component.dataset.collapsed = isCollapsed ? 'false' : 'true';

      // Update aria-expanded for accessibility
      const button = component.querySelector('.pc-configurator__collapse-toggle');
      if (button) {
        button.setAttribute('aria-expanded', isCollapsed ? 'true' : 'false');
      }
    }

    handleComponentDeselection(componentType) {
      // Remove from selected components
      delete this.selectedComponents[componentType];

      // Remove visual selection
      document.querySelectorAll(`[data-component-type="${componentType}"]`).forEach(option => {
        option.classList.remove('selected');
      });

      // Update pricing
      this.updateTotalPrice();

      // Update selected components list
      this.updateSelectedComponentsList();

      // Update selected component name in header to show "No selection"
      this.updateSelectedComponentName(componentType, null);

      // Re-apply compatibility rules (may enable previously disabled options)
      this.applyCompatibilityRules();

      // Update add to cart button
      this.updateAddToCartButton();
    }

    handleComponentSelection(event) {
      const radio = event.target;
      const option = radio.closest('.pc-configurator__option');
      const componentType = (option.dataset.componentType || '').toLowerCase();
      const optionName = option.dataset.optionName;
      const optionPrice = parseInt(option.dataset.optionPrice) || 0;
      const optionDescription = option.dataset.optionDescription || '';

      if (!componentType) {
        return;
      }

      // Update selected components
      this.selectedComponents[componentType] = {
        name: optionName,
        price: optionPrice,
        description: optionDescription
      };

      // Update visual selection
      this.updateOptionSelection(componentType, option);

      // Update pricing
      this.updateTotalPrice();

      // Update selected components list
      this.updateSelectedComponentsList();

      // Update selected component name in header
      this.updateSelectedComponentName(componentType, optionName);

      // Apply compatibility rules (disable incompatible options)
      this.applyCompatibilityRules();

      // Update build preview image if case is selected
      if (componentType === 'case') {
        this.updateBuildPreviewImage(option);
      }

      // Update add to cart button
      this.updateAddToCartButton();
    }

    updateOptionSelection(componentType, selectedOption) {
      // Remove selected class from all options of this component type
      document.querySelectorAll(`[data-component-type="${componentType}"]`).forEach(option => {
        option.classList.remove('selected');
      });

      // Add selected class to the chosen option
      selectedOption.classList.add('selected');

      // Update price displays to show differences
      this.updatePriceDisplays(componentType, selectedOption);
    }

    updatePriceDisplays(componentType, selectedOption) {
      const selectedPrice = parseInt(selectedOption.dataset.optionPrice);
      const allOptions = document.querySelectorAll(`[data-component-type="${componentType}"]`);

      allOptions.forEach(option => {
        const optionPrice = parseInt(option.dataset.optionPrice);
        const priceElement = option.querySelector('.pc-configurator__option-price');

        if (!priceElement) return;

        const priceDiff = optionPrice - selectedPrice;

        if (option === selectedOption) {
          // Selected option shows no price difference
          priceElement.textContent = 'Selected';
          priceElement.className = 'pc-configurator__option-price pc-configurator__option-price--selected';
        } else if (priceDiff > 0) {
          // More expensive than selected - show +price
          priceElement.textContent = `+$${priceDiff}`;
          priceElement.className = 'pc-configurator__option-price pc-configurator__option-price--more';
        } else if (priceDiff < 0) {
          // Cheaper than selected - show -price
          priceElement.textContent = `-$${Math.abs(priceDiff)}`;
          priceElement.className = 'pc-configurator__option-price pc-configurator__option-price--less';
        } else {
          // Same price
          priceElement.textContent = '+$0';
          priceElement.className = 'pc-configurator__option-price pc-configurator__option-price--same';
        }
      });
    }

    updateBuildPreviewImage(caseOption) {
      const buildImage = document.getElementById('build-preview-image');
      if (!buildImage) return;

      // Get the image from the case option
      const imageElement = caseOption.querySelector('.pc-configurator__option-image img');

      if (imageElement && imageElement.src) {
        buildImage.src = imageElement.src;
        buildImage.classList.add('visible');
      } else {
        buildImage.src = '';
        buildImage.classList.remove('visible');
      }
    }

    updateTotalPrice() {
      const componentsTotal = Object.values(this.selectedComponents).reduce((sum, component) => {
        return sum + component.price;
      }, 0);

      this.totalPrice = componentsTotal;

      // Update components total in breakdown
      const componentsTotalElement = document.querySelector('[data-components-total]');
      if (componentsTotalElement) {
        componentsTotalElement.textContent = `$${componentsTotal}`;
      }
    }

    updateSelectedComponentName(componentType, componentName) {
      // Normalize component type to lowercase for consistency
      const normalizedType = (componentType || '').toLowerCase();

      // Find the component section by data-component-type
      // Try lowercase first (as set by Liquid template)
      let componentSection = document.querySelector(`[data-component-type="${normalizedType}"]`);

      // If not found, try original case
      if (!componentSection && componentType) {
        componentSection = document.querySelector(`[data-component-type="${componentType}"]`);
      }

      if (!componentSection) {
        return;
      }

      // Find the selected component name element
      const selectedNameElement = componentSection.querySelector('[data-selected-component-name]');
      if (!selectedNameElement) return;

      // Update the text content
      const labelElement = selectedNameElement.querySelector('.pc-configurator__component-selected-label');
      if (labelElement) {
        labelElement.textContent = componentName || 'No selection';
      } else {
        selectedNameElement.innerHTML = `<span class="pc-configurator__component-selected-label">${componentName || 'No selection'}</span>`;
      }
    }

    updateSelectedComponentsList() {
      const listElement = document.querySelector('[data-selected-components-list]');
      if (!listElement) return;

      // Clear existing list
      listElement.innerHTML = '';

      console.log('selectedComponents before updating list:', this.selectedComponents);

      if (Object.keys(this.selectedComponents).length === 0) {
        const emptyItem = document.createElement('li');
        emptyItem.className = 'pc-configurator__selected-components-empty';
        emptyItem.textContent = 'No components selected yet';
        listElement.appendChild(emptyItem);
        return;
      }

      // Component type labels mapping
      const componentLabels = {
        'case': 'Case',
        'motherboard': 'Motherboard',
        'processor': 'Processor',
        'cpu': 'CPU',
        'ram': 'RAM',
        'storage': 'Storage',
        'gpu': 'GPU',
        'power_supply': 'Power Supply',
        'psu': 'PSU',
        'cooler': 'Cooler',
        'cooling': 'Cooling',
        'thermal_paste': 'Thermal Paste',
        'cable_extension': 'Cable Extensions',
        'additional_items': 'Additional Items'
      };

      // Get component sections in DOM order to maintain consistent display order
      const componentSections = document.querySelectorAll('.pc-configurator__component');
      const orderedComponents = [];

      // Iterate through sections in DOM order
      componentSections.forEach(section => {
        const firstOption = section.querySelector('.pc-configurator__option');
        if (firstOption) {
          const componentType = firstOption.dataset.componentType?.toLowerCase();
          if (componentType && this.selectedComponents[componentType]) {
            orderedComponents.push([componentType, this.selectedComponents[componentType]]);
          }
        }
      });

      console.log('Updating selected components list with ordered components:', orderedComponents);

      // Display components in DOM order
      orderedComponents.forEach(([type, component]) => {
        const listItem = document.createElement('li');
        listItem.className = 'pc-configurator__selected-components-item';

        const label = componentLabels[type.toLowerCase()] || type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
        const name = component.name || 'Unknown';
        const price = component.price || 0;

        listItem.innerHTML = `
        <span class="pc-configurator__selected-components-label">${label}:</span>
        <span class="pc-configurator__selected-components-name">${name}</span>
        ${this.enablePriceBreakdown ? `<span class="pc-configurator__selected-components-price">$${price}</span>` : ''}
      `;

        listElement.appendChild(listItem);
      });
    }

    updateAddToCartButton() {
      const addToCartBtn = document.querySelector('.pc-configurator__add-to-cart');
      if (!addToCartBtn) {
        return;
      }

      // Get all required components
      const requiredComponents = document.querySelectorAll('.pc-configurator__component[data-required="true"]');
      const requiredComponentTypes = Array.from(requiredComponents).map(comp =>
        comp.dataset.componentType?.toLowerCase()
      ).filter(Boolean);

      // Check if all required components are selected
      const missingRequired = requiredComponentTypes.filter(type =>
        !this.selectedComponents[type]
      );

      const isComplete = missingRequired.length === 0;

      addToCartBtn.disabled = !isComplete;

      if (isComplete) {
        addToCartBtn.textContent = `Checkout - $${this.totalPrice}`;
      } else {
        const selectedRequiredCount = requiredComponentTypes.length - missingRequired.length;
        addToCartBtn.textContent = `Select Required Components (${selectedRequiredCount}/${requiredComponentTypes.length})`;
      }
    }

    /**
     * Check if two options are incompatible based on compatibility rules
     */
    isIncompatible(component1, option1, component2, option2) {
      if (!this.compatibilityRules || this.compatibilityRules.length === 0) {
        return false;
      }

      // Normalize component types and option names for comparison
      const norm1 = { component: (component1 || '').toLowerCase(), option: (option1 || '').toLowerCase() };
      const norm2 = { component: (component2 || '').toLowerCase(), option: (option2 || '').toLowerCase() };

      return this.compatibilityRules.some(rule => {
        const rule1 = {
          component: (rule.component1 || '').toLowerCase(),
          option: (rule.option1 || '').toLowerCase()
        };
        const rule2 = {
          component: (rule.component2 || '').toLowerCase(),
          option: (rule.option2 || '').toLowerCase()
        };

        // Check if the rule matches either direction (A incompatible with B means B incompatible with A)
        const match1 = (norm1.component === rule1.component && norm1.option === rule1.option) &&
          (norm2.component === rule2.component && norm2.option === rule2.option);
        const match2 = (norm1.component === rule2.component && norm1.option === rule2.option) &&
          (norm2.component === rule1.component && norm2.option === rule1.option);

        return match1 || match2;
      });
    }

    /**
     * Apply compatibility rules - disable incompatible options based on current selections
     */
    applyCompatibilityRules() {
      if (!this.compatibilityRules || this.compatibilityRules.length === 0) {
        // Enable all options if no rules
        document.querySelectorAll('.pc-configurator__option').forEach(option => {
          this.setOptionEnabled(option, true);
        });
        return;
      }

      // Get all options
      const allOptions = document.querySelectorAll('.pc-configurator__option');

      allOptions.forEach(option => {
        const optionComponentType = (option.dataset.componentType || '').toLowerCase();
        const optionName = (option.dataset.optionName || '').toLowerCase();

        // Check if this option is incompatible with any selected component
        let isIncompatible = false;
        let incompatibleReason = '';

        Object.entries(this.selectedComponents).forEach(([selectedType, selectedComponent]) => {
          const selectedName = (selectedComponent.name || '').toLowerCase();

          // Skip if checking against itself
          if (selectedType.toLowerCase() === optionComponentType && selectedName === optionName) {
            return;
          }

          // Check if incompatible
          if (this.isIncompatible(selectedType, selectedName, optionComponentType, optionName)) {
            isIncompatible = true;
            // Find the rule to get the reason
            const rule = this.compatibilityRules.find(r => {
              const r1 = { component: (r.component1 || '').toLowerCase(), option: (r.option1 || '').toLowerCase() };
              const r2 = { component: (r.component2 || '').toLowerCase(), option: (r.option2 || '').toLowerCase() };
              const opt = { component: optionComponentType, option: optionName };
              const sel = { component: selectedType, option: selectedName };

              return ((r1.component === sel.component && r1.option === sel.option && r2.component === opt.component && r2.option === opt.option) ||
                (r2.component === sel.component && r2.option === sel.option && r1.component === opt.component && r1.option === opt.option));
            });

            if (rule && rule.reason) {
              incompatibleReason = rule.reason;
            } else {
              incompatibleReason = `Incompatible with selected ${selectedType}: ${selectedComponent.name}`;
            }
          }
        });

        // Enable or disable the option
        this.setOptionEnabled(option, !isIncompatible, incompatibleReason);
      });
    }

    /**
     * Enable or disable an option with visual feedback
     */
    setOptionEnabled(option, enabled, reason = '') {
      const radio = option.querySelector('input[type="radio"]');
      const optionElement = option;

      if (!radio) return;

      if (enabled) {
        radio.disabled = false;
        optionElement.classList.remove('pc-configurator__option--disabled');
        // Remove tooltip if it exists
        const existingTooltip = optionElement.querySelector('.pc-configurator__option-tooltip');
        if (existingTooltip) {
          existingTooltip.remove();
        }
        optionElement.removeAttribute('title');
      } else {
        // Don't disable if it's already selected
        if (radio.checked) {
          return; // Keep current selection enabled
        }

        radio.disabled = true;
        optionElement.classList.add('pc-configurator__option--disabled');

        // Remove existing tooltip if any
        const existingTooltip = optionElement.querySelector('.pc-configurator__option-tooltip');
        if (existingTooltip) {
          existingTooltip.remove();
        }

        // Create custom tooltip if reason is provided
        if (reason) {
          const tooltip = document.createElement('div');
          tooltip.className = 'pc-configurator__option-tooltip';
          tooltip.textContent = reason;
          optionElement.appendChild(tooltip);
          optionElement.setAttribute('title', reason); // Fallback for accessibility

          // Add hover event listeners for tooltip
          optionElement.addEventListener('mouseenter', () => {
            tooltip.style.opacity = '1';
            tooltip.style.visibility = 'visible';
          });

          optionElement.addEventListener('mouseleave', () => {
            tooltip.style.opacity = '0';
            tooltip.style.visibility = 'hidden';
          });
        }
      }
    }

    async addToCart() {
      if (this.isLoading) return;

      this.isLoading = true;
      this.showLoadingState();

      // Build line item properties for the current configuration
      const properties = this.buildLineItemProperties();

      // Get base PC variant ID from Liquid-injected global
      let variantId = this.getBasePCVariantId();

      if (!variantId || isNaN(parseInt(variantId, 10))) {
        this.hideLoadingState();
        this.showErrorMessage('Product variant ID is missing or invalid. Please ensure the product has at least one available variant.');
        this.isLoading = false;
        return;
      }

      const numericVariantId = parseInt(variantId, 10);

      // Calculate total price (including shipping if configured)
      let totalPrice = this.totalPrice;

      // If shipping product is configured, fetch its price and add to total
      if (this.shippingProductId) {
        try {
          const productResponse = await fetch(`/products.json?limit=1&ids=${this.shippingProductId}`);
          const productData = await productResponse.json();

          // Find the variant in the response
          if (productData.products && productData.products.length > 0) {
            const product = productData.products[0];
            const variant = product.variants.find(v => v.id === this.shippingProductId);

            if (variant && variant.price) {
              const shippingPrice = parseFloat(variant.price);
              totalPrice += shippingPrice;

              // Add shipping to properties
              properties['Shipping'] = `$${shippingPrice.toFixed(2)}`;

              console.log('Added shipping cost:', shippingPrice, 'New total:', totalPrice);
            }
          }
        } catch (error) {
          console.error('Error fetching shipping product price:', error);
          // Continue without shipping - don't fail the entire order
        }
      }

      // Get build preview image URL
      const buildImage = document.getElementById('build-preview-image');
      const imageUrl = (buildImage && buildImage.src && buildImage.classList.contains('visible'))
        ? buildImage.src
        : null;

      // Prepare draft order request
      const draftOrderData = {
        variantId: numericVariantId,
        quantity: 1,
        properties: properties,
        totalPrice: totalPrice,
        imageUrl: imageUrl
      };

      console.log('Creating draft order with data:', draftOrderData);

      // Call backend to create draft order
      fetch(`${this.backendUrl}/api/create-draft-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(draftOrderData)
      })
        .then(response => {
          if (!response.ok) {
            return response.json().then(errorData => {
              throw new Error(errorData.error || errorData.message || 'Failed to create draft order');
            });
          }
          return response.json();
        })
        .then(data => {
          console.log('Draft order created:', data);

          this.hideLoadingState();
          this.isLoading = false;

          // Redirect to Shopify invoice URL
          if (data.draftOrder && data.draftOrder.invoiceUrl) {
            window.location.href = data.draftOrder.invoiceUrl;
          } else {
            throw new Error('No invoice URL returned from draft order creation');
          }
        })
        .catch(error => {
          console.error('Error creating draft order:', error);
          this.hideLoadingState();
          this.showErrorMessage(error.message || 'Unknown error occurred while creating your order.');
          this.isLoading = false;
        });
    }

    // Patch cart drawer to handle sold-out products when removing items
    patchCartDrawerForSoldOutProducts(cartDrawer) {
      if (!cartDrawer || cartDrawer._pcConfiguratorPatched) return;

      // Override updateQuantity to handle sold-out products
      cartDrawer.updateQuantity = function (line, quantity) {
        const lineItem = this.querySelector(`#CartDrawerItem-${line}`);
        if (lineItem) {
          lineItem.classList.add('thb-loading');
        }

        const body = JSON.stringify({
          line,
          quantity,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname
        });

        dispatchCustomEvent('line-item:change:start', {
          quantity: quantity
        });

        fetch(`${window.theme.routes.cart_change_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: body
        })
          .then((response) => {
            if (!response.ok) {
              // If removing item (quantity = 0) and product is sold out, use alternative method
              if (quantity === '0' || quantity === 0) {
                console.log('Product sold out, using /cart/clear to remove all items');
                // Shopify doesn't allow removing sold-out items via API
                // The only workaround is to build a new updates object with all other items
                return fetch('/cart.js')
                  .then(res => res.json())
                  .then(cart => {
                    // Build updates object: set all items to their current quantity except the one we're removing
                    const updates = {};
                    cart.items.forEach((item, index) => {
                      // Line numbers are 1-based
                      if (index + 1 === parseInt(line)) {
                        updates[index + 1] = 0; // Set to 0 to remove
                      } else {
                        updates[index + 1] = item.quantity; // Keep current quantity
                      }
                    });

                    // Use /cart/update.js with all items
                    return fetch('/cart/update.js', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                      },
                      body: JSON.stringify({
                        updates: updates
                      })
                    })
                      .then(() => {
                        // After updating, fetch sections to update UI
                        const sections = this.getSectionsToRender().map((section) => section.section).join(',');
                        return fetch(`${window.location.pathname}?sections=${sections}`);
                      });
                  });
              }
              throw new Error('Failed to update cart');
            }
            return response;
          })
          .then((response) => response.text())
          .then((state) => {
            const parsedState = JSON.parse(state);

            this.getSectionsToRender().forEach((section => {
              const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

              if (parsedState.sections) {
                elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
              }
            }));

            this.removeProductEvent();
            dispatchCustomEvent('line-item:change:end', {
              quantity: quantity,
              cart: parsedState
            });

            if (lineItem) {
              lineItem.classList.remove('thb-loading');
            }
          })
          .catch((error) => {
            console.error('Cart update error:', error);
            if (lineItem) {
              lineItem.classList.remove('thb-loading');
            }
            // Refresh the cart drawer to show current state
            this.refresh();
          });
      };

      // Mark as patched to avoid double-patching
      cartDrawer._pcConfiguratorPatched = true;
    }

    buildLineItemProperties() {
      // Create properties for all selected components
      const properties = {};

      // Add each component as a property
      Object.entries(this.selectedComponents).forEach(([type, component]) => {
        properties[`${type.toUpperCase()} - ${component.name}`] = `$${component.price}`;
      });

      // Add build preview image URL as a property (using _ prefix to hide from display)
      const buildImage = document.getElementById('build-preview-image');
      if (buildImage && buildImage.src && buildImage.classList.contains('visible')) {
        properties['_build_image'] = buildImage.src;
      }

      // Add total price as a property in cents (using _ prefix to hide from display)
      // totalPrice is in dollars, convert to cents for storage
      properties['_total_price'] = (this.totalPrice * 100).toString();

      return properties;
    }

    updateCartItemImages() {
      // Fetch cart data to get items with _build_image and _total_price properties
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          if (!cart.items || cart.items.length === 0) return;

          // Helper function to format price
          const formatPrice = (price) => {
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(price / 100);
          };

          // Helper function to find cart item by properties (more reliable than index)
          const findCartItemByProperties = (items, properties) => {
            return items.findIndex(item => {
              if (!item.properties) return false;
              // Check if item has _build_image or _total_price property (indicating it's a custom PC)
              return item.properties._build_image || item.properties._total_price;
            });
          };

          // Find items with _build_image or _total_price property
          cart.items.forEach((item, index) => {
            if (item.properties && (item.properties._build_image || item.properties._total_price)) {
              // Use both index-based and key-based lookup for reliability
              const itemKey = item.key;
              const itemIndex = index + 1;

              // Update image if _build_image property exists
              if (item.properties._build_image) {
                const buildImageUrl = item.properties._build_image;

                // Update image in cart drawer - try multiple selectors
                let cartDrawerItem = document.getElementById(`CartDrawerItem-${itemIndex}`);
                if (!cartDrawerItem) {
                  // Try finding by data attributes or other methods
                  cartDrawerItem = document.querySelector(`[data-index="${itemIndex}"]`)?.closest('.product-cart-item')?.closest('[id^="CartDrawerItem"]');
                }

                if (cartDrawerItem) {
                  const imageContainer = cartDrawerItem.querySelector('.product-cart-item-image');
                  if (imageContainer) {
                    const img = imageContainer.querySelector('img');
                    if (img) {
                      img.src = buildImageUrl;
                      img.srcset = buildImageUrl;
                      // Also update any picture/source elements
                      const picture = imageContainer.querySelector('picture');
                      if (picture) {
                        const pictureImg = picture.querySelector('img');
                        if (pictureImg) {
                          pictureImg.src = buildImageUrl;
                          pictureImg.srcset = buildImageUrl;
                        }
                      }
                    }
                  }
                }

                // Update image in cart page
                let cartPageItemForImage = document.getElementById(`CartItem-${itemIndex}`);
                if (!cartPageItemForImage) {
                  cartPageItemForImage = document.querySelector(`[data-product-id="${itemKey}"]`);
                }

                if (cartPageItemForImage) {
                  const imageContainer = cartPageItemForImage.querySelector('.product-cart-item-image');
                  if (imageContainer) {
                    const img = imageContainer.querySelector('img');
                    if (img) {
                      img.src = buildImageUrl;
                      img.srcset = buildImageUrl;
                      // Also update any picture/source elements
                      const picture = imageContainer.querySelector('picture');
                      if (picture) {
                        const pictureImg = picture.querySelector('img');
                        if (pictureImg) {
                          pictureImg.src = buildImageUrl;
                          pictureImg.srcset = buildImageUrl;
                        }
                      }
                    }
                  }
                }
              }

              // Update price if _total_price property exists
              if (item.properties._total_price) {
                const totalPriceCents = parseInt(item.properties._total_price, 10); // Already in cents
                const formattedPrice = formatPrice(totalPriceCents);

                // Update price in cart drawer - update all price locations
                // Use both index-based and key-based lookup for reliability
                let cartDrawerItem = document.getElementById(`CartDrawerItem-${itemIndex}`);
                if (!cartDrawerItem) {
                  cartDrawerItem = document.querySelector(`[data-index="${itemIndex}"]`)?.closest('.product-cart-item')?.closest('[id^="CartDrawerItem"]');
                }

                if (cartDrawerItem) {
                  // Update price in product-cart-item-info
                  const priceInfoElements = cartDrawerItem.querySelectorAll('.product-cart-item-info .price .amount');
                  priceInfoElements.forEach(el => {
                    el.textContent = formattedPrice;
                  });

                  // Update price in product-cart-item-price
                  const priceItemElements = cartDrawerItem.querySelectorAll('.product-cart-item-price .price .amount');
                  priceItemElements.forEach(el => {
                    el.textContent = formattedPrice;
                  });

                  // Also update del/ins elements if they exist
                  const delElements = cartDrawerItem.querySelectorAll('.price del .amount');
                  const insElements = cartDrawerItem.querySelectorAll('.price ins .amount');
                  delElements.forEach(el => el.textContent = formattedPrice);
                  insElements.forEach(el => el.textContent = formattedPrice);
                }

                // Update price in cart page (product subtotal column)
                let cartPageItem = document.getElementById(`CartItem-${itemIndex}`);
                if (!cartPageItem) {
                  cartPageItem = document.querySelector(`[data-product-id="${itemKey}"]`);
                }

                if (cartPageItem) {
                  const priceElements = cartPageItem.querySelectorAll('.product-subtotal .price .amount');
                  priceElements.forEach(el => {
                    el.textContent = formattedPrice;
                  });
                  // Also update del/ins elements if they exist
                  const delElements = cartPageItem.querySelectorAll('.product-subtotal .price del .amount');
                  const insElements = cartPageItem.querySelectorAll('.product-subtotal .price ins .amount');
                  delElements.forEach(el => el.textContent = formattedPrice);
                  insElements.forEach(el => el.textContent = formattedPrice);

                  // Update data-subtotal attribute if it exists
                  const subtotalElement = cartPageItem.querySelector('.product-subtotal');
                  if (subtotalElement && subtotalElement.hasAttribute('data-subtotal')) {
                    subtotalElement.setAttribute('data-subtotal', totalPriceCents.toString());
                  }
                }
              }
            }
          });

          // Re-initialize cart drawer event handlers (for remove buttons, etc.)
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.removeProductEvent === 'function') {
            cartDrawer.removeProductEvent();
          }
        })
        .catch(() => {
          // Silently fail if cart fetch doesn't work
        });
    }

    getBasePCVariantId() {
      // Try multiple methods to get the variant ID, in order of reliability

      // Method 1: Get from product form (most reliable - uses actual form on page)
      const productForm = document.querySelector('form[action*="/cart/add"], form[data-type="add-to-cart-form"]');
      if (productForm) {
        const variantInput = productForm.querySelector('input[name="id"]');
        if (variantInput && variantInput.value) {
          const variantId = parseInt(variantInput.value, 10);
          if (!isNaN(variantId) && variantId > 0) {
            return variantId;
          }
        }
      }

      // Method 2: Get from Liquid-injected global
      if (window.pcConfiguratorProductVariantId) {
        const variantId = typeof window.pcConfiguratorProductVariantId === 'string'
          ? parseInt(window.pcConfiguratorProductVariantId, 10)
          : window.pcConfiguratorProductVariantId;

        if (!isNaN(variantId) && variantId > 0) {
          return variantId;
        }
      }

      // Method 3: Try to get from Shopify theme product object
      if (window.Shopify && window.Shopify.theme && window.Shopify.theme.product) {
        const product = window.Shopify.theme.product;
        if (product.selected_or_first_available_variant && product.selected_or_first_available_variant.id) {
          const variantId = product.selected_or_first_available_variant.id;
          return variantId;
        }
      }

      return null;
    }

    showLoadingState() {
      const addToCartBtn = document.querySelector('.pc-configurator__add-to-cart');
      if (addToCartBtn) {
        addToCartBtn.classList.add('pc-configurator__loading');
        addToCartBtn.disabled = true;
        addToCartBtn.textContent = 'Creating your order...';
      }
    }

    hideLoadingState() {
      const addToCartBtn = document.querySelector('.pc-configurator__add-to-cart');
      if (addToCartBtn) {
        addToCartBtn.classList.remove('pc-configurator__loading');
        addToCartBtn.disabled = false;
      }
    }

    showSuccessMessage() {
      // Create success message
      const message = document.createElement('div');
      message.className = 'pc-configurator__message pc-configurator__message--success';
      message.innerHTML = `
      <div class="pc-configurator__message-content">
        <h4>PC Build Added to Cart!</h4>
        <p>Your custom PC configuration has been added to your cart. You can now proceed to checkout or continue shopping.</p>
        <div class="pc-configurator__message-actions">
          <a href="/cart" class="btn btn--primary">View Cart</a>
          <button class="btn btn--secondary pc-configurator__message-close">Continue Building</button>
        </div>
      </div>
    `;

      document.body.appendChild(message);

      // Add close functionality
      message.querySelector('.pc-configurator__message-close').addEventListener('click', () => {
        message.remove();
      });

      // Auto-remove after 5 seconds
      setTimeout(() => {
        if (message.parentNode) {
          message.remove();
        }
      }, 5000);
    }

    showErrorMessage(errorText = '') {
      // Create error message
      const message = document.createElement('div');
      message.className = 'pc-configurator__message pc-configurator__message--error';
      const errorDetails = errorText ? `<p style="font-size: 0.9em; color: #ff6b6b; margin-top: 0.5em;">${errorText}</p>` : '';
      message.innerHTML = `
      <div class="pc-configurator__message-content">
        <h4>Error Adding to Cart</h4>
        <p>There was an error adding your PC build to the cart. Please try again.</p>
        ${errorDetails}
        <div class="pc-configurator__message-actions">
          <button class="btn btn--primary pc-configurator__message-close">Try Again</button>
        </div>
      </div>
    `;

      document.body.appendChild(message);

      // Add close functionality
      message.querySelector('.pc-configurator__message-close').addEventListener('click', () => {
        message.remove();
      });
    }
  }

  // Add message styles
  const messageStyles = `
    .pc-configurator__message {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: #2a2a2a;
      border: 1px solid #404040;
      border-radius: 12px;
      padding: 2rem;
      z-index: 1000;
      max-width: 500px;
      width: 90%;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    }

    .pc-configurator__message--success {
      border-color: #4CAF50;
    }

    .pc-configurator__message--error {
      border-color: #f44336;
    }

    .pc-configurator__message-content h4 {
      color: #ffffff;
      margin: 0 0 1rem 0;
      font-size: 1.2rem;
    }

    .pc-configurator__message-content p {
      color: #cccccc;
      margin: 0 0 1.5rem 0;
      line-height: 1.5;
    }

    .pc-configurator__message-actions {
      display: flex;
      gap: 1rem;
      justify-content: center;
    }

    .pc-configurator__message-close {
      cursor: pointer;
    }
  `;

  // Inject message styles
  const styleSheet = document.createElement('style');
  styleSheet.textContent = messageStyles;
  document.head.appendChild(styleSheet);
}
